import { useEffect, useRef, useState } from 'react'
import type { ApiError } from '@/types/api'
import { useAuthStore } from '@/features/auth/store/authStore'
import { useChurchesQuery } from '@/features/churches/hooks/useChurchesQuery'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { useDonationsQuery } from '@/features/donations/hooks'
import { useDonationsStore } from '@/features/donations/store/donationsStore'
import { useMembersQuery } from '@/features/members/hooks'
import { useMembersStore } from '@/features/members/store/membersStore'
import { useUsersQuery } from '@/features/users/hooks'
import { useUsersStore } from '@/features/users/store/usersStore'
import { notifyRequestError } from '@/utils/notifyRequestError'
import { usePermissions } from '@/utils/permissions'
import {
  useCategoriesQuery,
  useInventoryStockQuery,
  useTransactionsQuery,
} from '@/features/inventory/hooks'
import { useInventoryStore } from '@/features/inventory/store/inventoryStore'
import { useExpensesQuery } from '@/features/expenses/hooks/expenses/useExpensesQuery'
import { useExpensesStore } from '@/features/expenses/store/expensesStore'
import { useExpensesCategoriesQuery } from '@/features/expenses/hooks'

export function useInitializeData() {
  const authed = useAuthStore((s) => s.isAuthenticated)
  const user = useAuthStore((s) => s.user)
  const permissions = usePermissions()
  const prevAuthedRef = useRef(authed)

  const [isLoading, setIsLoading] = useState(true)
  const [hasError, setHasError] = useState(false)

  const shouldLoadData = authed && user

  // Queries
  const usersQuery = useUsersQuery({
    enabled: !!shouldLoadData,
  })
  const churchesQuery = useChurchesQuery({
    enabled: !!shouldLoadData,
  })
  const membersQuery = useMembersQuery({
    enabled: !!shouldLoadData,
  })
  const donationsQuery = useDonationsQuery({
    enabled: !!shouldLoadData && permissions.canAccessDonations(),
  })
  const inventoryStockQuery = useInventoryStockQuery({
    enabled: !!shouldLoadData && permissions.canAccessInventory(),
  })
  const inventoryCategoriesQuery = useCategoriesQuery({
    enabled: !!shouldLoadData && permissions.canAccessInventory(),
  })
  const inventoryTransactionsQuery = useTransactionsQuery({
    enabled: !!shouldLoadData && permissions.canAccessInventory(),
  })
  const expensesQuery = useExpensesQuery({
    enabled: !!shouldLoadData && permissions.canAccessExpenses(),
  })
  const expensesCategoriesQuery = useExpensesCategoriesQuery({
    enabled: !!shouldLoadData && permissions.canAccessExpenses(),
  })

  // Sync data to stores
  useEffect(() => {
    if (usersQuery.data) useUsersStore.getState().setUsers(usersQuery.data)
    if (churchesQuery.data)
      useChurchesStore.getState().setChurches(churchesQuery.data)
    if (membersQuery.data)
      useMembersStore.getState().setMembers(membersQuery.data)
    if (donationsQuery.data)
      useDonationsStore.getState().setDonations(donationsQuery.data)
    if (inventoryStockQuery.data)
      useInventoryStore.getState().setInventoryStock(inventoryStockQuery.data)
    if (inventoryCategoriesQuery.data)
      useInventoryStore.getState().setCategories(inventoryCategoriesQuery.data)
    if (inventoryTransactionsQuery.data)
      useInventoryStore
        .getState()
        .setTransactions(inventoryTransactionsQuery.data)
    if (expensesQuery.data)
      useExpensesStore.getState().setExpenses(expensesQuery.data)
    if (expensesCategoriesQuery.data)
      useExpensesStore.getState().setCategories(expensesCategoriesQuery.data)
  }, [
    usersQuery.data,
    churchesQuery.data,
    membersQuery.data,
    donationsQuery.data,
    inventoryStockQuery.data,
    inventoryCategoriesQuery.data,
    inventoryTransactionsQuery.data,
    expensesQuery.data,
    expensesCategoriesQuery.data,
  ])

  const queries = [
    { query: usersQuery, name: 'users' },
    { query: churchesQuery, name: 'churches' },
    { query: membersQuery, name: 'members' },
    { query: donationsQuery, name: 'donations' },
    { query: inventoryStockQuery, name: 'inventory stock items' },
    { query: inventoryCategoriesQuery, name: 'inventory categories' },
    { query: inventoryTransactionsQuery, name: 'inventory transactions' },
    { query: expensesQuery, name: 'expenses' },
    { query: expensesCategoriesQuery, name: 'expenses categories' },
  ]

  // Handle errors
  useEffect(
    () => {
      const has401 = queries.some(
        ({ query }) =>
          (query.error as ApiError | null | undefined)?.response?.status ===
          401,
      )

      if (has401) {
        setIsLoading(false)
        setHasError(true)
        return
      }

      queries.forEach(({ query, name }) => {
        const status = (query.error as ApiError | null | undefined)?.response
          ?.status
        if (query.error && status !== 401) {
          notifyRequestError(query.error as ApiError, `Failed to fetch ${name}`)
        }
      })
    },
    queries.map(({ query }) => query.error),
  )

  // Handle logout
  useEffect(() => {
    if (prevAuthedRef.current && !authed) {
      console.log('User logged out, stopping loader')
      setIsLoading(false)
      setHasError(false)
    }
    prevAuthedRef.current = authed
  }, [authed])

  // Calculate loading/error states
  useEffect(() => {
    if (!shouldLoadData) {
      setIsLoading(false)
      setHasError(false)
      return
    }

    const anyLoading = queries.some(
      ({ query }) => query.isEnabled && (query.isLoading || query.isFetching),
    )
    const anyError = queries.some(
      ({ query }) =>
        query.isError &&
        (query.error as ApiError | null | undefined)?.response?.status !== 401,
    )

    setIsLoading(anyLoading)
    setHasError(anyError)
  }, [
    shouldLoadData,
    ...queries.flatMap(({ query }) => [
      query.isLoading,
      query.isFetching,
      query.isEnabled,
      query.isError,
      query.error,
    ]),
  ])

  return { isLoading, hasError }
}
